import { Nullable } from "@/types/utils";

export enum MFAStatus {
  MFARequired = "mfa_required",
  MFASetupRequired = "mfa_setup_required",
  Success = "success",
}

// This will be expanded as new MFA options (authenticator, SSO, etc) are added,
// currently the only implemented option is SMS.
export enum MFAMethod {
  SMS = "sms",
}

export enum UserRole {
  Admin = "admin",
  Editor = "editor",
  Reviewer = "reviewer",
}

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  [UserRole.Admin]:
    "Can manage other users, edit interviews, and see responses",
  [UserRole.Editor]: "Can edit interviews and see responses",
  [UserRole.Reviewer]:
    "Can view interviews and responses and add new locations, but cannot edit anything",
};

export enum UserStatus {
  Active = "active",
  Inactive = "inactive",
  Invited = "invited",
}

export enum UserCapability {
  ManageCompanies = "manage_companies",
  ManageClientFit = "manage_client_fit",
}

interface BaseCompany {
  atsDomain: string | null;
  atsSystem: string;
  domain: string;
  id: string;
  isInternal: boolean;
  /**
   * AS-1855: drives the screener-builder version gate. True when every
   * non-deleted screener on this company has been retyped to 3.0 / 3.0m;
   * migrated companies can only create new 3.0 screeners, non-migrated
   * companies can only create new 2.0 screeners. The matching BE guard
   * lives at `screener_builder/util/migration-gate.util.ts`.
   */
  isMigratedTo30: boolean;
  mfaMethods: Nullable<MFAMethod[]>;
  name: string;
  retoolCompanyId: string;
}

interface ServerCompany extends BaseCompany {
  createdAt: Nullable<string>;
  updatedAt: Nullable<string>;
}

export interface Company extends BaseCompany {
  createdAt: Nullable<Date>;
  updatedAt: Nullable<Date>;
}

interface BaseUser {
  canSendCandidatePdfs: boolean;
  capabilities: UserCapability[];
  companyImpersonationId?: string;
  email: string;
  fullName?: string;
  id: string;
  isInternal: boolean;
  role: UserRole;
  status: UserStatus;
}

export interface ServerUser extends BaseUser {
  company: ServerCompany;
  createdAt: Nullable<string>;
  deletedAt: Nullable<string>;
  updatedAt: Nullable<string>;
}

export interface User extends BaseUser {
  company: Company;
  createdAt: Nullable<Date>;
  deletedAt: Nullable<Date>;
  updatedAt: Nullable<Date>;
}

export interface UserListUser {
  email: string;
  fullName?: string;
  id: string;
}

export interface UserManagementListUser extends UserListUser {
  canSendCandidatePdfs: boolean;
  role: UserRole;
  status: UserStatus;
}

export interface DraftUser {
  tempId: string;
  name: string;
  email: string;
  role: UserRole | null;
  isDraft: true;
}

export interface UserManagementTableMeta {
  currentUserId?: string;
  isInvitePending: (tempId: string) => boolean;
  isReInvitePending: (userId: string) => boolean;
  onDeactivate: (userId: string) => void;
  onInvite: (draft: DraftUser) => void;
  onReInvite: (userId: string) => void;
  onRemoveDraft: (tempId: string) => void;
  onRoleChange: (userId: string, role: UserRole) => void;
  onSendPdfsChange: (userId: string, canSendCandidatePdfs: boolean) => void;
  onUpdateDraft: (
    tempId: string,
    updates: Partial<Pick<DraftUser, "name" | "email" | "role">>,
  ) => void;
}

export interface UserContextType {
  canSendCandidatePdfs: boolean;
  capabilities: UserCapability[];
  // Company and internal status are also available on user, adding them as
  // separate properties for ease of access.
  company: Nullable<Company>;
  initialized: boolean;
  isInternal: boolean;
  login: (
    email: string,
    password: string,
    mfaToken?: string,
    phone?: string,
  ) => Promise<{
    maskedDestination?: string;
    status: MFAStatus;
    mfaEnrollmentToken?: string;
  }>;
  logOut: () => void;
  refreshUser: () => void;
  role: UserRole;
  user: Nullable<User>;
}
